import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { useAuth } from "@/hooks/useAuth";
import {
  knowledgeBaseApi,
  type KbAccuracyCorrectionPair,
  type KbAccuracyRecentSample,
  type KbClassificationAccuracy,
} from "@/api/knowledgeBase";
import { formatDateTimeInTz, useDateFormatter } from "@/utils/datetime";

// Knowledge Base AI-Assisted Uploader v1.0 PR 3 — read-only accuracy view.
// Sourced entirely from the audit rows the AI Upload flow already writes;
// no extra Claude spend.

type DayRange = "all" | "30" | "7";

const HIGH_CONFIDENCE = 0.75;

export function KnowledgeBaseClassificationAccuracyPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("KnowledgeBase.ManageDocuments");
  const [range, setRange] = useState<DayRange>("all");

  const days = range === "all" ? undefined : Number(range);

  const accuracyQ = useQuery({
    queryKey: ["kb", "admin", "classification-accuracy", range],
    queryFn: () => knowledgeBaseApi.getClassificationAccuracy(days),
    enabled: canManage,
  });

  if (!canManage) {
    return (
      <div className="py-space-6 text-small text-gray-mid">
        You do not have permission to view classification accuracy.
      </div>
    );
  }

  const data = accuracyQ.data;

  return (
    <>
      <PageHeader
        eyebrow="Admin · Knowledge Base"
        title="Classification Accuracy"
        description="How often Claude's AI Upload suggestions matched what the admin actually committed. Computed from the audit rows the AI Upload flow records — no extra Claude spend."
        actions={
          <Select
            value={range}
            onChange={(e) => setRange(e.target.value as DayRange)}
            aria-label="Filter by day range"
            className="w-44"
          >
            <option value="all">All time</option>
            <option value="30">Last 30 days</option>
            <option value="7">Last 7 days</option>
          </Select>
        }
      />

      {accuracyQ.isLoading && (
        <div className="py-space-6 text-center text-caption uppercase tracking-wide text-gray-mid">
          Loading…
        </div>
      )}

      {accuracyQ.isError && (
        <div className="py-space-5 text-center text-small text-status-error">
          {(accuracyQ.error as Error).message}
        </div>
      )}

      {data && data.volume.totalCommitted === 0 && (
        <div className="py-space-6 text-center text-small text-gray-mid">
          No committed classifications yet — accuracy appears here once you
          commit an AI Upload batch.
        </div>
      )}

      {data && data.volume.totalCommitted > 0 && (
        <AccuracyBody data={data} />
      )}
    </>
  );
}

function AccuracyBody({ data }: { data: KbClassificationAccuracy }) {
  const { dateTime, tz } = useDateFormatter();
  const recentColumns = buildRecentColumns(tz);
  return (
    <div className="flex flex-col gap-space-5">
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-space-3">
        <StatCard
          label="Committed (graded)"
          value={String(data.volume.totalCommitted)}
          sub={`of ${data.volume.totalClassified} classified · ${data.volume.distinctBatches} batches`}
        />
        <StatCard
          label="Category accuracy"
          value={formatPercent(data.category.accuracyRate)}
          sub={`${data.category.sampleCount} graded`}
        />
        <StatCard
          label="Tag F1"
          value={formatPercent(data.tags.f1)}
          sub={`P ${formatPercent(data.tags.precision)} · R ${formatPercent(data.tags.recall)}`}
        />
        <StatCard
          label="Total Claude cost"
          value={formatUsd(data.cost.totalEstimatedCostUsd)}
          sub={`${formatUsd(data.cost.meanCostUsdPerDoc ?? 0)} / doc · ${data.cost.rowCount} rows`}
        />
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-space-3">
        <StatCard
          label="Title kept verbatim"
          value={formatPercent(data.title.keptVerbatimRate)}
          sub={`${data.title.sampleCount} samples`}
        />
        <StatCard
          label="Description kept verbatim"
          value={formatPercent(data.description.keptVerbatimRate)}
          sub={`${data.description.sampleCount} samples`}
        />
        <StatCard
          label="Tag exact-set match"
          value={formatPercent(data.tags.exactSetMatchRate)}
          sub={`${data.tags.sampleCount} samples`}
        />
        <StatCard
          label="Abandoned drafts"
          value={String(data.volume.totalAbandoned)}
          sub="Classified, never committed"
        />
      </section>

      <section>
        <h3 className="text-h4 font-display mb-space-2">Confidence calibration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
          <StatCard
            label="Mean confidence — category correct"
            value={formatPercent(data.confidenceCalibration.meanConfidenceWhenCorrect)}
            sub={`${data.confidenceCalibration.correctSampleCount} samples`}
          />
          <StatCard
            label="Mean confidence — category corrected"
            value={formatPercent(data.confidenceCalibration.meanConfidenceWhenCorrected)}
            sub={`${data.confidenceCalibration.correctedSampleCount} samples · the gap shows whether the ${formatPercent(HIGH_CONFIDENCE)} auto-select threshold is well-placed`}
          />
        </div>
      </section>

      <section>
        <h3 className="text-h4 font-display mb-space-2">Supersession</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-space-3">
          <StatCard
            label="Precision (when suggested)"
            value={formatPercent(data.supersession.precision)}
            sub={`${data.supersession.correctCount} correct of ${data.supersession.suggestedCount} suggested`}
          />
          <StatCard
            label="Recall (vs admin-accepted)"
            value={formatPercent(data.supersession.recall)}
            sub={`${data.supersession.correctCount} correct of ${data.supersession.acceptedCount} accepted`}
          />
          <StatCard
            label="Admin added (Claude missed)"
            value={String(
              Math.max(0, data.supersession.acceptedCount - data.supersession.correctCount),
            )}
            sub="Predecessor the admin linked that Claude didn't suggest"
          />
        </div>
      </section>

      <section>
        <h3 className="text-h4 font-display mb-space-2">
          Top category corrections
        </h3>
        <p className="text-caption text-gray-mid mb-space-2">
          Most common <code>suggested → accepted</code> pairs where the admin
          changed Claude's category.
        </p>
        <DataTable<KbAccuracyCorrectionPair>
          columns={correctionColumns}
          rows={data.category.topCorrections}
          rowKey={(p) => `${p.suggestedCategoryCode}->${p.acceptedCategoryCode}`}
          emptyMessage="No category corrections in this window."
        />
      </section>

      <section>
        <h3 className="text-h4 font-display mb-space-2">Recent committed samples</h3>
        <p className="text-caption text-gray-mid mb-space-2">
          The last {data.recentSamples.length} graded rows. Per-field "changed"
          flags show where the admin overrode Claude.
        </p>
        <DataTable<KbAccuracyRecentSample>
          columns={recentColumns}
          rows={data.recentSamples}
          rowKey={(s) => s.suggestionId}
          emptyMessage="No recent samples."
        />
      </section>

      <p className="text-caption text-gray-mid">
        Tokens: {data.cost.totalInputTokens.toLocaleString()} in /{" "}
        {data.cost.totalOutputTokens.toLocaleString()} out · generated{" "}
        {dateTime(data.generatedAtUtc)}
      </p>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded border border-gray-light bg-white p-space-3">
      <div className="text-caption uppercase tracking-wide text-gray-mid">
        {label}
      </div>
      <div className="text-h3 font-display mt-space-1">{value}</div>
      {sub && <div className="text-caption text-gray-mid mt-space-1">{sub}</div>}
    </div>
  );
}

const correctionColumns: Column<KbAccuracyCorrectionPair>[] = [
  {
    key: "suggested",
    header: "Suggested",
    render: (p) => <span className="font-mono text-small">{p.suggestedCategoryCode}</span>,
  },
  {
    key: "accepted",
    header: "Accepted",
    render: (p) => <span className="font-mono text-small">{p.acceptedCategoryCode}</span>,
  },
  {
    key: "count",
    header: "Count",
    render: (p) => <span className="tabular-nums">{p.count}</span>,
  },
];

function buildRecentColumns(tz: string): Column<KbAccuracyRecentSample>[] {
  return [
  {
    key: "doc",
    header: "Document",
    render: (s) => (
      <div className="min-w-0">
        <div className="truncate font-medium">
          {s.documentTitle ?? s.fileName ?? s.suggestionId.slice(0, 8)}
        </div>
        {s.fileName && s.documentTitle && (
          <div className="text-caption text-gray-mid truncate">{s.fileName}</div>
        )}
      </div>
    ),
  },
  {
    key: "category",
    header: "Category (suggested → accepted)",
    render: (s) => (
      <div className="flex items-center gap-space-1 text-small">
        <span className="font-mono">{s.suggestedCategoryCode ?? "—"}</span>
        <span className="text-gray-mid">→</span>
        <span className={"font-mono " + (s.categoryChanged ? "text-status-warning" : "")}>
          {s.acceptedCategoryCode ?? "—"}
        </span>
      </div>
    ),
  },
  {
    key: "confidence",
    header: "Confidence",
    render: (s) => <ConfidenceBadge value={s.suggestedConfidence ?? null} />,
  },
  {
    key: "changed",
    header: "Changed fields",
    render: (s) => {
      const changed: string[] = [];
      if (s.categoryChanged) changed.push("category");
      if (s.tagsChanged) changed.push("tags");
      if (s.titleChanged) changed.push("title");
      if (s.descriptionChanged) changed.push("description");
      if (s.supersessionChanged) changed.push("supersession");
      if (changed.length === 0) {
        return (
          <span className="inline-flex px-space-2 py-px rounded text-caption font-display font-semibold bg-status-success/15 text-status-success">
            Accepted as suggested
          </span>
        );
      }
      return (
        <span className="inline-flex flex-wrap gap-space-1">
          {changed.map((c) => (
            <span
              key={c}
              className="px-space-2 py-px rounded text-caption font-display font-semibold bg-status-warning/15 text-status-warning"
            >
              {c}
            </span>
          ))}
        </span>
      );
    },
  },
  {
    key: "when",
    header: "Committed",
    render: (s) => (
      <span className="text-caption text-gray-mid">
        {formatDateTimeInTz(s.acceptedAt, tz)}
      </span>
    ),
  },
  ];
}

function ConfidenceBadge({ value }: { value: number | null }) {
  if (value == null) {
    return <span className="text-caption text-gray-mid">—</span>;
  }
  const isHigh = value >= HIGH_CONFIDENCE;
  return (
    <span
      className={
        "inline-flex shrink-0 px-space-2 py-px rounded text-caption font-display font-semibold " +
        (isHigh
          ? "bg-status-success/15 text-status-success"
          : "bg-status-warning/15 text-status-warning")
      }
    >
      {formatPercent(value)}
    </span>
  );
}

function formatPercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

function formatUsd(value: number): string {
  return value.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 4,
  });
}
